import { z } from 'zod';
import { DashboardSchema, SCHEMA_VERSION, sourceReferences, tilesOf, type Dashboard, type Screen, type Tile } from './dashboard.ts';
import { MIGRATIONS } from './migrations.ts';
import { REDACTED, placeholderName, secretName, secretPlaceholder } from './secret.ts';

export type ParseResult = { ok: true; dashboard: Dashboard } | { ok: false; issues: string[] };


function isRecord(value: unknown) : value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Brings a dashboard file's JSON to the current schema version, or says why it cannot. */
export function migrateDashboard(json: Record<string, unknown>) : Record<string, unknown> | string {
    let current = json;
    let version = current.schemaVersion;
    if (typeof version !== 'number' || !Number.isInteger(version)) {
        return 'schemaVersion is missing: this is not a Tilez dashboard';
    }
    if (version > SCHEMA_VERSION) {
        return `schemaVersion ${version} is newer than this app understands (${SCHEMA_VERSION})`;
    }

    while (version < SCHEMA_VERSION) {
        const step = MIGRATIONS[version];
        if (!step) {
            return `no migration from schemaVersion ${version}`;
        }
        current = step(current);
        version = current.schemaVersion;
        if (typeof version !== 'number') {
            return 'a migration lost schemaVersion';
        }
    }

    return current;
}

/** The schema version a dashboard file says it is in; undefined when it is not JSON or says none. */
export function schemaVersionOf(text: string) : number | undefined {
    try {
        const json: unknown = JSON.parse(text);
        const version = isRecord(json) ? json.schemaVersion : undefined;

        return typeof version === 'number' && Number.isInteger(version) ? version : undefined;
    } catch {
        return undefined;
    }
}

/** A screen's tiles (not its groups), with where each is on the screen once read (`tiles[2]`). */
function tilesWithPaths(screen: Screen) : { tile: Tile; path: string }[] {
    return screen.tiles.flatMap((tile, index) => (tile.type === 'group' ? [] : [{ tile, path: `tiles[${index}]` }]));
}

/** Checks what the schema cannot: that names refer to connections and data sources that exist. */
function crossReferences(dashboard: Dashboard) : string[] {
    const issues: string[] = [];
    for (const [screenIndex, screen] of dashboard.screens.entries()) {
        for (const { tile, path } of tilesWithPaths(screen)) {
            const where = `screens[${screenIndex}].${path}${tile.label ? ` (${tile.label})` : ''}`;
            for (const [name, source] of Object.entries(tile.dataSources)) {
                if (!Object.hasOwn(dashboard.connections, source.connection)) {
                    issues.push(`${where}.dataSources.${name}: no connection named "${source.connection}"`);
                }
            }
            // A tile with no data sources at all is not set up yet (added in the editor, Phase 3): it
            // shows no data. One with sources must name only those it has.
            const isSetUp = Object.keys(tile.dataSources).length > 0;
            for (const [path, name] of isSetUp ? sourceReferences(tile.type, tile.options) : []) {
                if (!Object.hasOwn(tile.dataSources, name)) {
                    issues.push(`${where}.${path}: no data source named "${name}" in this tile`);
                }
            }
            if (tile.tap?.kind === 'command' && !Object.hasOwn(tile.dataSources, tile.tap.source)) {
                issues.push(`${where}.tap.source: no data source named "${tile.tap.source}" in this tile`);
            }
        }
    }

    return issues;
}

/** Parses a dashboard file: JSON, migration to the current version, schema, cross-references. */
export function parseDashboard(input: string | unknown) : ParseResult {
    let json: unknown = input;
    if (typeof input === 'string') {
        try {
            json = JSON.parse(input);
        } catch (error) {
            return { ok: false, issues: [`not JSON: ${error instanceof Error ? error.message : String(error)}`] };
        }
    }
    if (!isRecord(json)) {
        return { ok: false, issues: ['not a JSON object'] };
    }

    const migrated = migrateDashboard(json);
    if (typeof migrated === 'string') {
        return { ok: false, issues: [migrated] };
    }

    const parsed = DashboardSchema.safeParse(migrated);
    if (!parsed.success) {
        return {
            ok: false,
            issues: parsed.error.issues.map((issue) => `${issue.path.map(String).join('.') || '(top)'}: ${issue.message}`),
        };
    }

    const issues = crossReferences(parsed.data);

    return issues.length > 0 ? { ok: false, issues } : { ok: true, dashboard: parsed.data };
}

/**
 * A copy with every credential replaced by `***`: the fields a driver's config schema marks secret
 * (`secretKeysOf` gives them per driver; unknown drivers keep their config) and camera URLs.
 * For logs and exports that leave the device (decision §12).
 */
export function redactDashboard(dashboard: Dashboard, secretKeysOf: (driver: string) => readonly string[]) : Dashboard {
    // A dashboard is plain JSON, so a JSON round trip is a full copy.
    const copy = JSON.parse(JSON.stringify(dashboard)) as Dashboard;
    for (const connection of Object.values(copy.connections)) {
        for (const key of secretKeysOf(connection.driver)) {
            if (Object.hasOwn(connection.config, key)) {
                connection.config[key] = REDACTED;
            }
        }
    }
    for (const screen of copy.screens) {
        for (const tile of tilesOf(screen)) {
            if (tile.type === 'video' && 'url' in tile.options) {
                tile.options.url = REDACTED;
            }
        }
    }

    return copy;
}

/**
 * A copy with each credential replaced by its placeholder, and the values they stand for, for a
 * store that keeps credentials apart (docs/specs/secrets.md SE-3). The credentials are the fields a
 * driver's config schema marks secret; an empty field, and one that holds a placeholder already,
 * stay as they are.
 */
export function splitSecrets(dashboard: Dashboard, secretKeysOf: (driver: string) => readonly string[]) : { dashboard: Dashboard; values: Record<string, string> } {
    const copy = JSON.parse(JSON.stringify(dashboard)) as Dashboard;
    const values: Record<string, string> = {};

    for (const [id, connection] of Object.entries(copy.connections)) {
        for (const key of secretKeysOf(connection.driver)) {
            const value = connection.config[key];

            if (typeof value !== 'string' || value === '' || placeholderName(value) !== undefined) {
                continue;
            }

            const name = secretName(id, key);

            values[name] = value;
            connection.config[key] = secretPlaceholder(name);
        }
    }

    return { dashboard: copy, values };
}

/** A copy with each placeholder that has a value filled in; the others stay placeholders (SE-2, SE-3). */
export function joinSecrets(dashboard: Dashboard, values: Readonly<Record<string, string>>) : Dashboard {
    const copy = JSON.parse(JSON.stringify(dashboard)) as Dashboard;

    for (const connection of Object.values(copy.connections)) {
        for (const [key, value] of Object.entries(connection.config)) {
            const name = placeholderName(value);

            if (name !== undefined && Object.hasOwn(values, name)) {
                connection.config[key] = values[name];
            }
        }
    }

    return copy;
}

/** The connections of a dashboard file as JSON; undefined for one that has none in this form. */
function fileConnections(file: unknown) : Record<string, { config?: Record<string, unknown> }> | undefined {
    const connections = (file as { connections?: unknown } | null)?.connections;

    return typeof connections === 'object' && connections !== null ? (connections as Record<string, { config?: Record<string, unknown> }>) : undefined;
}

function filePlaceholders(file: unknown) : string[] {
    return Object.values(fileConnections(file) ?? {}).flatMap((connection) =>
        Object.values(connection.config ?? {}).flatMap((value) => {
            const name = placeholderName(value);

            return name === undefined ? [] : [name];
        }),
    );
}

/** A file written back as the one that was read: its indentation and its last newline. */
function fileText(file: unknown, like: string) : string {
    const indent = /\n([ \t]+)"/.exec(like)?.[1] ?? 2;

    return `${JSON.stringify(file, null, indent)}${like.endsWith('\n') ? '\n' : ''}`;
}

/**
 * `splitSecrets` on a dashboard file's text, for those that keep the file as it was written (the
 * servers, the app's stores): the text with placeholders, the values split out, and every
 * placeholder the text then holds. The text is kept byte for byte when nothing was split; undefined
 * when it is not JSON with connections.
 */
export function splitSecretsInText(text: string, secretKeysOf: (driver: string) => readonly string[]) : { text: string; values: Record<string, string>; placeholders: string[] } | undefined {
    let file: unknown;

    try {
        file = JSON.parse(text);
    } catch {
        return undefined;
    }

    if (!fileConnections(file)) {
        return undefined;
    }

    // The file as JSON has the connections a parsed dashboard has; the rest is not touched.
    const parts = splitSecrets(file as Dashboard, secretKeysOf);
    const changed = Object.keys(parts.values).length > 0;

    return { text: changed ? fileText(parts.dashboard, text) : text, values: parts.values, placeholders: filePlaceholders(parts.dashboard) };
}

/** `joinSecrets` on a dashboard file's text; the text as it was when it has no placeholder with a value. */
export function joinSecretsInText(text: string, values: Readonly<Record<string, string>>) : string {
    if (Object.keys(values).length === 0) {
        return text;
    }

    let file: unknown;

    try {
        file = JSON.parse(text);
    } catch {
        return text;
    }

    return filePlaceholders(file).some((name) => Object.hasOwn(values, name)) ? fileText(joinSecrets(file as Dashboard, values), text) : text;
}

/** The placeholders a dashboard file's text holds (SE-1). */
export function placeholdersInText(text: string) : string[] {
    try {
        return filePlaceholders(JSON.parse(text));
    } catch {
        return [];
    }
}

/** A connection's config as its driver gets it: a placeholder nothing resolved is left out, as a missing value is (SE-2). */
export function withoutPlaceholders(config: Readonly<Record<string, unknown>>) : Record<string, unknown> {
    return Object.fromEntries(Object.entries(config).filter(([, value]) => placeholderName(value) === undefined));
}

/** JSON Schema of the dashboard file, for editors and hand-written dashboards (defaults are optional). */
export function dashboardJsonSchema() : Record<string, unknown> {
    return z.toJSONSchema(DashboardSchema, { io: 'input' }) as Record<string, unknown>;
}
